import * as amplitude from "@amplitude/analytics-react-native"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import TextM from "@common/components/text/TextM"
import { useTutorCheckpointManager } from "@common/hooks/checkpoint/TutorCheckpointManager"
import {
    useAgentNotifCount,
    useCompletedConversation,
    useIsOngoingCheckpoint,
    useIsParticipantOnline,
    useIsSoundMode,
    useParticipantActiveTime,
    useTimeSinceLastCall,
} from "@common/hooks/livekit/useParticipantState"
import { ParticipantTileM } from "@common/livekit/components-web/ParticipantTileM"
import CheckpointLaunchPopup from "@common/liveMode/liveV3/CheckpointLaunchPopup"
import { TIME_SINCE_LAST_CALL_MINUTES } from "@common/liveMode/StudentsList"
import { theme } from "@common/theme/theme"
import { DimensionsContext, isMobile } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import {
    markConversationAsRead,
    setIsLiveModeEnabled,
    setShowRaiseHandCheckpoint,
} from "@data/redux/actions/liveMode"
import { setMuteVideo } from "@data/redux/actions/videoCall"
import { liveService } from "@data/services/liveService"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import { hasRole } from "@data/utility/ability"
import { hasFeature } from "@data/utility/Utils"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { Dimensions, View } from "react-native"
import { Portal } from "react-native-paper"
import { connect } from "react-redux"
import LiveAvatarM from "../../../tutor/students/LiveAvatarM"
import { AdditionalActions } from "../../../tutor/students/NewStudentsListItem"
import ConversationSummaryPopup from "./ConversationSummaryPopup"
import LiveParticipantIndicator from "./LiveParticipantIndicator"
import { useTutorEventsPanelEnabled } from "./tutorEventsPanel/TutorEventsPanel"

const LiveParticipant = (props) => {
    const {
        user,
        style,
        containerStyle,
        onParticipantClick = null,
        onTrackClick = null,
        isSelected = false,
        hideTile = false,
        hiddenTrackSid = null,
        userTracks: userTracksProp = [],
        setLiveChatUserId = () => {},
        setAgentCheckpointsUserId = () => {},
        showPopUp = false,
        noAdditionalActions = false,
        compact = false,
    } = props

    const dimensions = useContext(DimensionsContext)

    // Per-user hooks — each instance only re-renders when ITS value changes
    const isSoundOn = useIsSoundMode(user?.id)
    const isOngoingCheckpoint = useIsOngoingCheckpoint(user?.id)
    const isOnline = useIsParticipantOnline(user?.id, props.currentUser?.id)
    const activeTime = useParticipantActiveTime(user?.id)
    const timeSinceLastCall = useTimeSinceLastCall(user?.id)
    const agentNotifCount = useAgentNotifCount(user?.id, props.liveSessionId)
    const completedConversation = useCompletedConversation(user?.id)
    const tutorEventsPanelEnabled = useTutorEventsPanelEnabled()

    // Memoized — was inlined in mapStateToProps and recomputed on every
    // Redux update (incl. 5s metric ticks) across all student tiles.
    const areAllStudentsInSilentMode = useMemo(() => {
        const students =
            props.currentLiveSession?.users?.filter(
                (participant) => !participant?.is_tutor
            ) ?? []
        const soundModeIds = props.soundModeIds ?? []
        return (
            students.length > 0 &&
            students.every((student) => !soundModeIds.includes(student?.id))
        )
    }, [props.currentLiveSession?.users, props.soundModeIds])

    const setSoundModeApiRequest = liveServiceV2.setSoundModeV2
    const lowerHandsApi = liveServiceV2.lowerHandsV2
    const isV4 = hasFeature(
        props.currentLiveSession?.live_room,
        "checkpoint-rh"
    )
    const isIndividual =
        props.currentLiveSession?.live_room?.type === "individual"


    const handleSoundModeToggle = () => {
        // In individual sessions, audio must stay on — show explanation dialog
        if (isIndividual && isSoundOn) {
            DialogM.show({
                champion: "abou",
                text1: "Session individuelle",
                text2: "En session individuelle, la communication audio doit rester activée entre le tuteur et l'élève.",
                buttonText1: "OK",
            })
            return
        }

        const isInSilentMode = !isSoundOn
        const canAutoSelectStudent =
            isInSilentMode &&
            areAllStudentsInSilentMode &&
            typeof onParticipantClick === "function"

        if (canAutoSelectStudent) {
            onParticipantClick(user?.id)
        }

        // Toggling sound mode from a participant tile only affects THAT student.
        // Tutors routinely keep several students in Dialogue at the same time,
        // so switching one in must never mute the others. The "pin one student
        // by muting the rest" behaviour is intentional only from the Event Panel
        // CTA, which lives in useTutorEventsActions (pinStudentInDialogueAtomic).
        const applyToggle = () => {
            props.apiRequest(
                setSoundModeApiRequest,
                { id: props.liveSessionId },
                {
                    enabled: !isSoundOn,
                    user_ids: [user?.id],
                }
            )
            props.apiRequest(
                lowerHandsApi,
                { id: props.liveSessionId },
                { user_ids: [user?.id] }
            )
        }

        if (isOngoingCheckpoint && isInSilentMode) {
            props.setShowRaiseHandCheckpoint(user?.id, false)

            DialogM.show({
                champion: "sam",
                variant: "main",
                text1: `${user?.display_name} est en plein point d'avancement avec Sam`,
                text2: "Souhaites-tu interrompre la conversation pour dialoguer avec l'élève ?",
                buttonText1: "Oui, interrompre",
                buttonColor1: theme.colors.primary,
                onPress: () => {
                    amplitude.track("checkpoint.interrupt.accept")

                    const activeConversation =
                        user?.conversations?.[user?.conversations?.length - 1]

                    if (activeConversation?.id) {
                        props.apiRequest(
                            liveService.finishConversation,
                            { conversation_id: activeConversation.id },
                            {
                                interruption_reason: "tutor_interruption",
                                ended_at: new Date().toISOString(),
                            }
                        )
                    }

                    applyToggle()
                },
                buttonText2: "Non",
                onPress2: () => {
                    amplitude.track("checkpoint.interrupt.reject")
                },
            })
        } else {
            applyToggle()
        }
    }

    const countMessages =
        props.currentLiveSession?.unread_private_messages_count?.[user?.id]

    const additionalActions = (item) => {
        return [
            ...(hasFeature(
                props.currentLiveSession?.live_room,
                "checkpoint-rh"
            ) && user?.conversations?.length > 0
                ? [
                      {
                          icon: "robot",
                          backgroundColor: theme.colors.blackLight,
                          label: "",
                          color: theme.colors.white,
                          borderColor: theme.colors.white,
                          borderWidth: 1,
                          request: false,
                          fontSize: 10,
                          borderRadius: 15,
                          tooltipTitle: "Points d'avancement Sam",
                          badgeCounter: agentNotifCount,
                          onPress: () => {
                              setAgentCheckpointsUserId(item.item?.id)
                          },
                      },
                  ]
                : []),
            {
                icon: "comment",
                backgroundColor: theme.colors.blackLight,
                label: "",
                color: theme.colors.white,
                borderColor: theme.colors.white,
                borderWidth: 1,
                request: false,
                fontSize: 10,
                borderRadius: 15,
                tooltipTitle: "Messages",
                badgeCounter: countMessages,
                onPress: () => {
                    setLiveChatUserId(item.item?.id)
                },
            },
        ]
    }

    // Tracks are now passed from parent — filter out hidden track locally
    const userTracks = hiddenTrackSid
        ? userTracksProp?.filter((track) => track.sid !== hiddenTrackSid)
        : userTracksProp

    const isTimeSinceLastCallAlert =
        timeSinceLastCall >= TIME_SINCE_LAST_CALL_MINUTES * 60

    const rightPanelWidth = Dimensions.get("window").width / 8

    const placeholderMode = hideTile || !isOnline || userTracks?.length === 0
    const height = (placeholderMode && !isOnline ? 120 : 140) + 40

    const isSoundOnAction = {
        icon: !isSoundOn ? "volume-mute" : "volume-up",
        backgroundColor: isOnline
            ? theme.colors.whiteTransparentMore
            : theme.colors.blackTransparent,
        label: null,
        color: !isOnline
            ? theme.colors.light
            : !isSoundOn
            ? theme.colors.purple
            : theme.colors.success,
        tooltipTitle: !isSoundOn ? "Activer le son" : "Désactiver le son",
        loadingColor: theme.colors.white,
        request:
            (props.setSoundModeRequest &&
                props.setSoundModeRequestUserIds?.includes(user?.id)) ||
            (props.setSoundModeV2Request &&
                props.setSoundModeV2RequestUserIds?.includes(user?.id)),
        disabled: !isOnline,
        fontSize: 10,
        borderRadius: 15,
        borderWidth: !isOnline ? 0 : 3,
        borderColor: !isOnline
            ? theme.colors.light
            : !isSoundOn
            ? theme.colors.purple
            : theme.colors.success,
        onPress: handleSoundModeToggle,
    }

    const { showCheckpointPopup, isSilentModePopup, handleCheckpointAction } =
        useTutorCheckpointManager({
            user,
            liveSessionId: props.liveSessionId,
            currentUser: props.currentUser,
            activeTime,
            isTimeSinceLastCallAlert,
            currentLiveSession: props.currentLiveSession,
            isStudentOnline: isOnline,
            skipCheckpoint: tutorEventsPanelEnabled,
        })

    const [showingConversation, setShowingConversation] = useState(false)
    const [conversationSummary, setConversationSummary] = useState(null)
    const [shownConversationIds, setShownConversationIds] = useState(new Set())

    useEffect(() => {
        if (!completedConversation?.conversation?.id) return
        if (shownConversationIds.has(completedConversation.conversation.id)) return

        setShownConversationIds(
            (prev) =>
                new Set([...prev, completedConversation.conversation.id])
        )
        setConversationSummary(completedConversation)

        PlaySoundM.play(
            require("../../../../assets/sounds/jingles_STEEL16.mp3")
        )

        setShowingConversation(true)
    }, [completedConversation])

    const handleCloseConversation = () => {
        setShowingConversation(false)
        if (conversationSummary?.conversation?.id) {
            props.markConversationAsRead(
                conversationSummary.liveSessionId,
                conversationSummary.studentId,
                conversationSummary.conversation.id
            )
        }
    }

    // I am adding a helper function `renderParticipantPopups` to centralize popup rendering for both layouts.
    const renderParticipantPopups = (insideGrid) => {
        if (!isV4 || !hasRole(props.currentUser, "tutor")) {
            return null
        }
        const pointerEvents =
            showCheckpointPopup || showingConversation ? "auto" : "none"
        const commonPopups = (
            <>
                {showCheckpointPopup && (
                    <CheckpointLaunchPopup
                        visible={showCheckpointPopup}
                        student={user}
                        onAccept={(isAutoClick) =>
                            handleCheckpointAction(
                                true,
                                isAutoClick ? "auto" : "tutor_accept"
                            )
                        }
                        onReject={() =>
                            handleCheckpointAction(false, "tutor_accept")
                        }
                        width={380}
                        studentCardHeight={180}
                        arrowPosition="left"
                        description={
                            isSilentModePopup
                                ? `🕒 ${user?.display_name} travaille seul depuis 10 min. Je lui demande où il en est, t'es d'accord ?`
                                : `🖐 ${user?.display_name} vient de lever la main, est-ce que je lui demande ce qu'il veut ?`
                        }
                    />
                )}
                {showingConversation && conversationSummary?.conversation && (
                    <ConversationSummaryPopup
                        visible={showingConversation}
                        onClose={handleCloseConversation}
                        conversation={conversationSummary.conversation}
                        studentName={user?.display_name}
                        arrowPosition="left"
                        width={380}
                        studentCardHeight={180}
                        duration={12000}
                        showProgressBar={true}
                    />
                )}
            </>
        )
        if (insideGrid && props.isGridLayout) {
            return (
                <View
                    style={{
                        position: "absolute",
                        top: showCheckpointPopup ? -12 : 0,
                        right: dimensions?.width <= 700 ? -30 : 0,
                        zIndex: 9999 + (props.index || 0),
                    }}
                    pointerEvents={pointerEvents}
                >
                    {commonPopups}
                </View>
            )
        }
        if (!insideGrid && !props.isGridLayout) {
            if (dimensions?.width <= 700) {
                return (
                    <Portal>
                        <View
                            style={{
                                position: "absolute",
                                top: "30%",
                                left: isMobile ? "60%" : "50%",
                                transform: [
                                    { translateX: -190 },
                                    { translateY: -90 },
                                ],
                                zIndex: 9999 + (props.index || 0),
                            }}
                            pointerEvents={pointerEvents}
                        >
                            {commonPopups}
                        </View>
                    </Portal>
                )
            }
            return (
                <Portal>
                    <View
                        style={{
                            position: "absolute",
                            top:
                                props.index * 180 +
                                (showCheckpointPopup ? 10 : 25),
                            right: rightPanelWidth,
                            zIndex: 9999 + (props.index || 0),
                        }}
                        pointerEvents={pointerEvents}
                    >
                        {commonPopups}
                    </View>
                </Portal>
            )
        }
        return null
    }

    return (
        <>
            <View
                key={user?.id}
                style={{
                    width: 220,
                    height: height,
                    ...style,
                }}
            >
                {renderParticipantPopups(true)}
                <View
                    style={{
                        flex: 1,
                        borderColor: isSelected
                            ? theme.colors.primary
                            : theme.colors.lightMore,
                        borderWidth: 1,
                        boxShadow: isSelected
                            ? `0 0px 0px 4px rgba(34, 63, 143, 1)`
                            : "none",
                        overflow: "hidden",
                        backgroundColor: theme.colors.blackAlmost,
                        ...containerStyle,
                    }}
                >
                    {
                        <TouchableRippleM
                            onPress={() => {
                                if (onParticipantClick) {
                                    onParticipantClick(user?.id)
                                }
                            }}
                            disabled={!onParticipantClick}
                            style={{
                                flex: 1,
                                cursor: onParticipantClick ? "pointer" : null,
                            }}
                        >
                            <View style={{ flex: 1, flexDirection: "row" }}>
                                {!placeholderMode ? (
                                    userTracks.map((track, index) => (
                                        <View key={index} style={{ flex: 1 }}>
                                            <ParticipantTileM
                                                participants={[user]}
                                                onParticipantClick={
                                                    onTrackClick
                                                }
                                                trackRef={track}
                                                style={{ flex: 1 }}
                                                noDisplayName={
                                                    noAdditionalActions ||
                                                    user?.id ===
                                                        props.currentUser?.id ||
                                                    index > 0
                                                }
                                                noOutOfApp={true}
                                            />
                                        </View>
                                    ))
                                ) : (
                                    <View
                                        style={{
                                            flex: 1,
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <View
                                            style={{
                                                justifyContent: "center",
                                                alignItems: "center",
                                                flexDirection: "row",
                                                marginBottom: 10,
                                            }}
                                        >
                                            <LiveAvatarM
                                                user={user}
                                                size={40}
                                                noOutOfApp={true}
                                            />
                                        </View>
                                        {!noAdditionalActions && (
                                            <TextM
                                                wrap
                                                color={theme.colors.white}
                                                fontSize={12}
                                                style={{
                                                    marginLeft: 5,
                                                    position: "absolute",
                                                    bottom: 6,
                                                    left: 0,
                                                    borderRadius: 5,
                                                    paddingHorizontal: 5,
                                                    backgroundColor:
                                                        theme.colors
                                                            .blackTransparent,
                                                }}
                                            >
                                                {user?.display_name}
                                                <TextM
                                                    color={theme.colors.white}
                                                    fontSize={9}
                                                >
                                                    {!isOnline
                                                        ? " | hors-ligne"
                                                        : ""}
                                                </TextM>
                                            </TextM>
                                        )}
                                    </View>
                                )}
                            </View>
                        </TouchableRippleM>
                    }
                    {!noAdditionalActions && !user?.is_tutor && (
                        <View
                            style={{
                                height: 50,
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                paddingHorizontal: 5,
                                backgroundColor: theme.colors.blackAlmost,
                                borderTopWidth: 1,
                                borderColor: theme.colors.lightMore,
                            }}
                        >
                            <View
                                style={{
                                    height: 40,
                                    flexDirection: "row",
                                    justifyContent: "center",
                                }}
                            >
                                <AdditionalActions
                                    additionalActions={[
                                        ...additionalActions(
                                            { item: user },
                                            isSoundOn,
                                            isOnline,
                                            activeTime
                                        ),
                                        ...(!user?.is_tutor
                                            ? [isSoundOnAction]
                                            : []),
                                    ]}
                                />
                            </View>
                        </View>
                    )}
                </View>
                <LiveParticipantIndicator
                    user={user}
                    liveSessionId={props.liveSessionId}
                    compact={compact}
                />
            </View>
            {showPopUp && renderParticipantPopups(false)}
        </>
    )
}

const mapStateToProps = (state) => {
    const currentLiveSession = state.data.currentUser?.object?.live_sessions?.find(
        (x) => x?.id === state.liveMode?.liveSessionId
    )
    const soundModeIds = state.liveMode?.soundModeIds ?? []

    return {
        currentUser: state.data.currentUser?.object,
        liveSessionId: state.liveMode?.liveSessionId,
        currentLiveSession,
        soundModeIds,
        setSoundModeRequest: state.api.setSoundMode?.request,
        setSoundModeV2Request: state.api.setSoundModeV2?.request,
        setSoundModeRequestUserIds: state.api.setSoundMode?.data?.user_ids,
        setSoundModeV2RequestUserIds: state.api.setSoundModeV2?.data?.user_ids,
        // `areAllStudentsInSilentMode` is now memoized in the component
        // (see useMemo above) — recomputing it here ran a `students.every()`
        // for every tile on every Redux update.
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setIsLiveModeEnabled,
    setMuteVideo,
    setShowRaiseHandCheckpoint,
    markConversationAsRead,
}
export default connect(mapStateToProps, mapDispatchToProps)(LiveParticipant)
